<template>
  <div class="user-menu-container">
    <!-- Menu Toggle Button -->
    <button @click="toggleMenu" class="user-menu-toggle" :class="{ 'active': isOpen }">
      <div class="user-avatar">
        <span v-if="userProfile.firstName">{{ userProfile.firstName.charAt(0).toUpperCase() }}</span>
        <span v-else>👤</span>
      </div>
      <span class="menu-arrow">▼</span>
    </button>

    <!-- Backdrop -->
    <div v-if="isOpen" class="dropdown-backdrop" @click="isOpen = false; $emit('menu-close')"></div>
    
    <!-- Dropdown Menu -->
    <div v-if="isOpen" class="user-menu-dropdown">
      <!-- User Info Section -->
      <div class="menu-section user-info">
        <div class="user-details">
          <h3>{{ userProfile.firstName }} {{ userProfile.lastName }}</h3>
          <p class="user-email">{{ user.email }}</p>
          <div class="subscription-status">
            <span v-if="isPremium" class="premium-badge">⭐ Premium Member</span>
            <span v-else class="free-badge">🔒 Free Plan</span>
          </div>
          <div v-if="isPremium && subscriptionDate" class="subscription-date">
            <p>Member since: {{ formatDate(subscriptionDate) }}</p>
          </div>
        </div>
      </div>

      <!-- Menu Items -->
      <div class="menu-section menu-items">
        <button @click="showProfile = true" class="menu-item" :class="{ 'needs-update': needsProfileUpdate }">
          <span class="menu-icon">👤</span>
          Profile Settings
          <span v-if="needsProfileUpdate" class="update-badge">!</span>
        </button>
        
        <button @click="showContact = true" class="menu-item">
          <span class="menu-icon">📧</span>
          Contact Support
        </button>
        
        <button v-if="isPremium" @click="showCancelSubscription = true" class="menu-item danger">
          <span class="menu-icon">❌</span>
          Cancel Subscription
        </button>
        
        <button @click="handleLogout" class="menu-item">
          <span class="menu-icon">🚪</span>
          Logout
        </button>
      </div>
    </div>

    <!-- Profile Modal -->
    <div v-if="showProfile" class="modal-overlay" @click="showProfile = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>Profile Settings</h2>
          <button @click="showProfile = false" class="modal-close">×</button>
        </div>
        <div class="modal-content">
          <div class="profile-info">
            <div class="info-group">
              <label>Name:</label>
              <p>{{ userProfile.firstName }} {{ userProfile.lastName }}</p>
            </div>
            <div class="info-group">
              <label>Email:</label>
              <p>{{ user.email }}</p>
            </div>
            <div class="info-group">
              <label>Grade Level:</label>
              <p>{{ userProfile.gradeLevel === 'K' ? 'Kindergarten' : `Grade ${userProfile.gradeLevel}` }}</p>
            </div>
            <div class="info-group">
              <label>Subscription Status:</label>
              <p v-if="isPremium">⭐ Premium Member</p>
              <p v-else>🔒 Free Plan</p>
            </div>
            <div v-if="isPremium && subscriptionDate" class="info-group">
              <label>Member Since:</label>
              <p>{{ formatDate(subscriptionDate) }}</p>
            </div>
            <div v-if="isPremium && nextBillingDate" class="info-group">
              <label>Next Billing Date:</label>
              <p>{{ formatDate(nextBillingDate) }}</p>
            </div>
          </div>
          <div class="standards-link-section">
            <router-link to="/standards" class="standards-link" @click="showProfile = false">
              📚 View Academic Standards
            </router-link>
          </div>
          <div class="modal-actions">
            <button @click="showEditProfile = true" class="btn btn-primary">
              Edit Profile
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Support Modal -->
    <div v-if="showContact" class="modal-overlay" @click="showContact = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>Contact Support</h2>
          <button @click="showContact = false" class="modal-close">×</button>
        </div>
        <div class="modal-content">
          <form @submit.prevent="submitContactForm" class="contact-form">
            <div class="form-group">
              <label for="contact-email">Your Email</label>
              <input 
                id="contact-email"
                v-model="contactForm.email" 
                type="email" 
                placeholder="your-email@example.com"
                required
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label for="contact-subject">Subject</label>
              <input 
                id="contact-subject"
                v-model="contactForm.subject" 
                type="text" 
                placeholder="What can we help you with?"
                required
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label for="contact-message">Message</label>
              <textarea 
                id="contact-message"
                v-model="contactForm.message" 
                placeholder="Please describe your issue or question..."
                rows="5"
                required
                class="form-textarea"
              ></textarea>
            </div>
            <button type="submit" :disabled="contactSubmitting" class="btn btn-primary">
              <span v-if="contactSubmitting">Sending...</span>
              <span v-else>Send Message</span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- Cancel Subscription Modal -->
    <div v-if="showCancelSubscription" class="modal-overlay" @click="showCancelSubscription = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>Cancel Subscription</h2>
          <button @click="showCancelSubscription = false" class="modal-close">×</button>
        </div>
        <div class="modal-content">
          <div class="cancel-warning">
            <h3>⚠️ Are you sure?</h3>
            <p>You're about to cancel your premium subscription. Here's what will happen:</p>
            <ul>
              <li>You'll lose access to premium features immediately</li>
              <li>You won't be charged again</li>
              <li>You can reactivate anytime</li>
            </ul>
            <p><strong>You can still use the free version of ChalkBoard AI.</strong></p>
          </div>
          <div class="modal-actions">
            <button @click="showCancelSubscription = false" class="btn btn-secondary">
              Keep Subscription
            </button>
            <button @click="cancelSubscription" :disabled="cancelling" class="btn btn-danger">
              <span v-if="cancelling">Cancelling...</span>
              <span v-else>Yes, Cancel Subscription</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal -->
    <div v-if="showEditProfile" class="modal-overlay" @click="showEditProfile = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>Edit Profile</h2>
          <button @click="showEditProfile = false" class="modal-close">×</button>
        </div>
        <div class="modal-content">
          <form @submit.prevent="updateProfile" class="profile-form">
            <div class="form-group">
              <label for="edit-firstname">First Name</label>
              <input 
                id="edit-firstname"
                v-model="editForm.firstName" 
                type="text" 
                placeholder="Enter your first name"
                required
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label for="edit-lastname">Last Name</label>
              <input 
                id="edit-lastname"
                v-model="editForm.lastName" 
                type="text" 
                placeholder="Enter your last name"
                required
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label for="edit-grade">Grade Level</label>
              <select 
                id="edit-grade"
                v-model="editForm.gradeLevel" 
                class="form-input"
                required
              >
                <option value="">Select your grade</option>
                <option v-for="grade in grades" :key="grade" :value="grade">
                  {{ grade === 'K' ? 'Kindergarten' : `Grade ${grade}` }}
                </option>
              </select>
            </div>
            <div class="modal-actions">
              <button type="button" @click="showEditProfile = false" class="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" :disabled="updating" class="btn btn-primary">
                <span v-if="updating">Updating...</span>
                <span v-else>Update Profile</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { getFirestore, doc, updateDoc } from 'firebase/firestore'
import emailjs from '@emailjs/browser'

export default {
  name: 'UserMenu',
  props: {
    user: {
      type: Object,
      required: true
    },
    userProfile: {
      type: Object,
      required: true
    },
    isPremium: {
      type: Boolean,
      required: true
    },
    subscriptionDate: {
      type: [Date, String],
      default: null
    },
    nextBillingDate: {
      type: [Date, String],
      default: null
    }
  },
  emits: ['logout', 'menu-open', 'menu-close'],
  setup(props, { emit }) {
    const isOpen = ref(false)
    const showProfile = ref(false)
    const showContact = ref(false)
    const showCancelSubscription = ref(false)
    const showEditProfile = ref(false)
    const contactSubmitting = ref(false)
    const cancelling = ref(false)
    const updating = ref(false)

    const contactForm = ref({
      subject: '',
      message: '',
      email: props.user.email
    })

    const editForm = ref({
      firstName: props.userProfile.firstName || '',
      lastName: props.userProfile.lastName || '',
      gradeLevel: props.userProfile.gradeLevel || ''
    })

    const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']

    // Check if profile needs updating
    const needsProfileUpdate = computed(() => {
      return !props.userProfile.lastName || 
             props.userProfile.firstName === 'Student' ||
             !props.userProfile.gradeLevel
    })

    const toggleMenu = () => {
      isOpen.value = !isOpen.value
      
      // Emit events to parent component
      if (isOpen.value) {
        emit('menu-open')
        nextTick(() => {
          adjustMenuPosition()
        })
      } else {
        emit('menu-close')
      }
    }

    const adjustMenuPosition = () => {
      const menuDropdown = document.querySelector('.user-menu-dropdown')
      if (!menuDropdown) return
      
      // Position dropdown lower on screen, centered horizontally
      // This ensures it's fully visible and not cut off at the top
      // Use !important to override any conflicting styles
      menuDropdown.style.setProperty('position', 'fixed', 'important')
      menuDropdown.style.setProperty('top', '30%', 'important')
      menuDropdown.style.setProperty('left', '50%', 'important')
      menuDropdown.style.setProperty('right', 'auto', 'important')
      menuDropdown.style.setProperty('bottom', 'auto', 'important')
      menuDropdown.style.setProperty('transform', 'translate(-50%, 0)', 'important')
      menuDropdown.style.setProperty('margin', '0', 'important')
      menuDropdown.style.setProperty('z-index', '999999', 'important')
    }

    // Handle clicking outside to close menu
    const handleClickOutside = (event) => {
      const menuContainer = event.target.closest('.user-menu-container')
      if (!menuContainer && isOpen.value) {
        isOpen.value = false
        emit('menu-close')
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleResize)
    })

    const handleResize = () => {
      if (isOpen.value) {
        adjustMenuPosition()
      }
    }

    // Watch for user changes to update email
    watch(() => props.user.email, (newEmail) => {
      if (newEmail && contactForm.value.email !== newEmail) {
        contactForm.value.email = newEmail
      }
    }, { immediate: true })

    // Watch for userProfile changes to update editForm
    watch(() => props.userProfile, (newProfile) => {
      editForm.value = {
        firstName: newProfile.firstName || '',
        lastName: newProfile.lastName || '',
        gradeLevel: newProfile.gradeLevel || ''
      }
    }, { immediate: true, deep: true })

    const formatDate = (date) => {
      if (!date) return 'N/A'
      try {
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        console.error('Error formatting date:', error)
        return 'N/A'
      }
    }

    const handleLogout = () => {
      isOpen.value = false
      emit('menu-close')
      emit('logout')
    }

    const submitContactForm = async () => {
      contactSubmitting.value = true
      try {
        
        // Send email using EmailJS
        const result = await emailjs.send(
          'service_tt9yanj', // Service ID
          'template_zkq83l2', // Template ID
          {
            from_name: `${props.userProfile.firstName} ${props.userProfile.lastName}`,
            from_email: contactForm.value.email,
            user_id: props.user.uid,
            subject: contactForm.value.subject,
            message: contactForm.value.message,
            to_email: 'support@chalkboardai.org'
          },
          '4gjVg0J-kkug1spSO' // Public Key from EmailJS
        )
        
        
        // Reset form
        contactForm.value = { subject: '', message: '', email: props.user.email }
        showContact.value = false
        
        // Show success message
        alert('Support request sent successfully! We\'ll get back to you within 24 hours.')
      } catch (error) {
        console.error('Error sending email:', error)
        
        // Fallback to mailto if EmailJS fails
        const subject = encodeURIComponent(contactForm.value.subject)
        const body = encodeURIComponent(`
From: ${contactForm.value.email}
Name: ${props.userProfile.firstName} ${props.userProfile.lastName}
User ID: ${props.user.uid}

Message:
${contactForm.value.message}

---
This message was sent from ChalkBoard AI support form.
        `)
        
        const mailtoLink = `mailto:support@chalkboardai.org?subject=${subject}&body=${body}`
        window.location.href = mailtoLink
        
        alert('Redirecting to your email client. Please send the pre-filled email.')
      } finally {
        contactSubmitting.value = false
      }
    }

    const cancelSubscription = async () => {
      cancelling.value = true
      try {
        // Update user profile to remove premium status
        const db = getFirestore()
        const userRef = doc(db, 'users', props.user.uid)
        
        await updateDoc(userRef, {
          isPremium: false,
          subscriptionId: null,
          subscriptionStatus: 'cancelled',
          cancelledAt: new Date()
        })

        // Close modal and refresh
        showCancelSubscription.value = false
        window.location.reload() // Simple refresh to update state
      } catch (error) {
        console.error('Error cancelling subscription:', error)
        alert('Error cancelling subscription. Please try again or contact support.')
      } finally {
        cancelling.value = false
      }
    }

    const updateProfile = async () => {
      updating.value = true
      try {
        const db = getFirestore()
        const userRef = doc(db, 'users', props.user.uid)
        
        await updateDoc(userRef, {
          firstName: editForm.value.firstName.trim(),
          lastName: editForm.value.lastName.trim(),
          gradeLevel: editForm.value.gradeLevel,
          updatedAt: new Date()
        })

        // Close modals
        showEditProfile.value = false
        showProfile.value = false
        
        // Show success message and emit event to parent to refresh user data
        alert('Profile updated successfully!')
        
        // Emit a custom event that the parent can listen to for refreshing data
        window.dispatchEvent(new CustomEvent('profile-updated'))
        
      } catch (error) {
        console.error('Error updating profile:', error)
        
        // Check if it's a quota error
        if (error.code === 'resource-exhausted' || error.message.includes('quota') || error.message.includes('limit')) {
          alert('We\'ve reached our daily database limit. Please try again tomorrow or contact support.')
        } else {
          alert('Error updating profile. Please try again.')
        }
      } finally {
        updating.value = false
      }
    }

    return {
      isOpen,
      showProfile,
      showContact,
      showCancelSubscription,
      showEditProfile,
      contactForm,
      editForm,
      grades,
      needsProfileUpdate,
      contactSubmitting,
      cancelling,
      updating,
      toggleMenu,
      formatDate,
      handleLogout,
      submitContactForm,
      cancelSubscription,
      updateProfile
    }
  }
}
</script>

<style scoped>
.user-menu-container {
  position: relative;
  display: inline-block;
  z-index: 999999;
}

.user-menu-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 8px 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.user-menu-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.user-menu-toggle.active {
  background: rgba(255, 255, 255, 0.3);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 14px;
}

.menu-arrow {
  font-size: 10px;
  transition: transform 0.3s ease;
}

.user-menu-toggle.active .menu-arrow {
  transform: rotate(180deg);
}

.user-menu-dropdown {
  position: fixed;
  top: 30%;
  left: 50%;
  transform: translate(-50%, 0);
  width: 280px;
  max-width: calc(100vw - 40px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 999999 !important;
  overflow: hidden;
  animation: slideDownCentered 0.3s ease;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
}

@media (max-width: 768px) {
  .user-menu-dropdown {
    width: 90%;
    max-width: 320px;
    max-height: calc(100vh - 80px);
  }
  
  .modal {
    max-height: 90vh;
    width: 95%;
  }
}

@keyframes slideDownCentered {
  from {
    opacity: 0;
    transform: translate(-50%, -20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.menu-section {
  padding: 16px;
}

.menu-section:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.user-info {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.user-details h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
}

.user-email {
  margin: 0 0 8px 0;
  font-size: 14px;
  opacity: 0.9;
}

.subscription-status {
  margin-bottom: 8px;
}

.premium-badge, .free-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.premium-badge {
  background: rgba(255, 215, 0, 0.2);
  color: #ffd700;
}

.free-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.subscription-date p {
  margin: 0;
  font-size: 12px;
  opacity: 0.8;
}

.menu-items {
  padding: 8px 0;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 8px;
  margin: 2px 0;
  color: #333;
  font-size: 14px;
}

.menu-item:hover {
  background: #f8f9fa;
}

.menu-item.danger {
  color: #dc3545;
}

.menu-item.danger:hover {
  background: #fff5f5;
}

.menu-item.needs-update {
  position: relative;
  background: #fff3cd;
  border-left: 3px solid #ffc107;
}

.menu-item.needs-update:hover {
  background: #ffeaa7;
}

.update-badge {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: #dc3545;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.menu-icon {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000 !important;
  backdrop-filter: blur(5px);
}

.modal {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  margin: auto;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.modal-close:hover {
  background: #f0f0f0;
}

.modal-content {
  padding: 24px;
}

.profile-info .info-group {
  margin-bottom: 16px;
}

.profile-info label {
  display: block;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  font-size: 14px;
}

.profile-info p {
  margin: 0;
  color: #333;
  font-size: 16px;
}

.contact-form .form-group,
.profile-form .form-group {
  margin-bottom: 16px;
}

.contact-form label,
.profile-form label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.form-input, .form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #f8f9fa;
  color: #333;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background: #e9ecef;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-warning {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.cancel-warning h3 {
  margin: 0 0 12px 0;
  color: #856404;
}

.cancel-warning p {
  margin: 0 0 12px 0;
  color: #856404;
}

.cancel-warning ul {
  margin: 0 0 12px 0;
  padding-left: 20px;
  color: #856404;
}

.cancel-warning li {
  margin-bottom: 4px;
}

.standards-link-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
}

.standards-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;
}

.standards-link:hover {
  color: #5568d3;
  text-decoration: underline;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-actions .btn {
  min-width: 120px;
}

/* Responsive */
@media (max-width: 768px) {
  .user-menu-dropdown {
    position: fixed !important;
    width: 260px;
    right: auto;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-height: calc(100vh - 150px);
  }
  
  .modal {
    width: 95%;
    margin: 20px;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .modal-actions .btn {
    width: 100%;
  }
}

/* Handle positioning when menu might get cut off */
/* Ensure menu doesn't get cut off on small screens */
@media (max-width: 480px) {
  .user-menu-dropdown {
    width: 90vw;
    max-width: 300px;
    max-height: 80vh;
  }
}

/* Backdrop for dropdown */
.dropdown-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999998 !important;
  backdrop-filter: blur(2px);
}
</style> 